import { useStore } from 'dg-web-shared/lib/Flux.tsx';
import * as CurrentOperatorLoginState from '../../common/state/CurrentOperatorLoginState.ts';
import { MainContent } from '../../shared-mui-components/MainContent.tsx';
import { HeaderWithButtons } from '../../shared-mui-components/HeaderWithButtons.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { DesignServices } from '@mui/icons-material';
import { useServerFetch } from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { SeparationBar } from '../../layout/components/SeparationBar.tsx';
import {
    AutoActive,
    PermittypeChannels,
} from 'product-shared/product-template/ProductOptions.tsx';
import { LoadingSpinnerPresets } from 'dg-web-shared/common/components/material-ui/PresetLoadingSpinner.tsx';
import { EmptyResults } from '../../shared-mui-components/ListMessages.tsx';
import { OperatorAsyncLoadedSection } from '../../app/components/OperatorAsyncLoadedSection.tsx';
import { PermitTypesList } from './PermitTypesList.tsx';
import { OperatorQuotaOutlet } from '../../quotas/OperatorQuotasDetail.tsx';
import { IntradayValidityWeekProfile } from 'dg-web-shared/model/IntradayValidityWeekProfile.ts';
import { QuotaPartitionInfo } from '../../common/state/PermitTypeState.ts';

export type PermitTypeListDTO = {
    permitTypeId: number;
    permitTypeDescription: string;
    zoneCount: number;
    zoneSelectionType: 'fixed' | 'single_choice' | 'multiple_choice';
    autoActive: AutoActive;
    needsClearance: boolean;
    dtaAccountDescription: string | null;
    showAccount: boolean;
};

type PermitTypeZone = {
    zoneId: number;
    zoneName: string;
};

export interface PermitTypeDetailDTO extends PermitTypeListDTO {
    isBadgePermit: boolean;
    maxIdentificationCount: number;
    zones: PermitTypeZone[];
    vatNumber: string | null;
    quotaPartitionInfo: QuotaPartitionInfo | null;
    intradayValidityWeekProfile: IntradayValidityWeekProfile | null;
    channels: PermittypeChannels;
    urlIdentification: string | null;
    withinValidityConstraintDates: string[] | null;
}

export const PermitTypes = () => {
    const [permitTypesState, refetchPermit] = useServerFetch<
        PermitTypeListDTO[],
        object
    >(() => ({ url: `/ui-api/operator-account/permit-type` }), {});

    const { storeState } = useStore(store => ({
        currentLogin: CurrentOperatorLoginState.get(store),
    }));

    if (!storeState.currentLogin.data) {
        return null;
    }

    return (
        <>
            <MainContent>
                <HeaderWithButtons
                    title={
                        <Localized
                            de="Bewilligungsarten"
                            fr="Types d'autorisation"
                            it="Tipi di autorizzazione"
                            en="Types of authorisation"
                        />
                    }
                    icon={DesignServices}
                />
                <SeparationBar />
                <OperatorAsyncLoadedSection
                    requestState={permitTypesState}
                    pendingLoaderPreset={
                        LoadingSpinnerPresets.FillAllSpaceAndCenter
                    }
                    render={permits =>
                        permits.length < 1 ? (
                            <EmptyResults />
                        ) : (
                            <PermitTypesList permits={permits} />
                        )
                    }
                />
            </MainContent>
            <OperatorQuotaOutlet context={{ refetchQuota: refetchPermit }} />
        </>
    );
};
